import { t } from "@/lib/i18n/interpolate";
import type {
  DocBlock,
  DocRun,
  DocSection,
  DocumentModel,
} from "@/lib/tool-kit/document";
import { allocateWords } from "@/lib/tool-kit/essay-templates/research-proposal";
import {
  countWords,
  parseWholeCount,
  splitLines,
} from "@/lib/tool-kit/essay-templates/text";

/**
 * A Specific Aims page template in the shape NIH institutes teach: an opening that
 * states the problem and the gap, an objective with a central hypothesis, two to four
 * aims each with an approach and an expected outcome, and a closing payoff.
 *
 * NIH fixes the page limit (one page) and says the aims should centre on the project's
 * goals, anticipated outcomes and overall impact; the paragraph-by-paragraph shape and
 * the word shares below are drafting conventions, not NIH rules. The writer's own
 * sentences are placed verbatim — the tool never words a claim for them — and an empty
 * field becomes a prompt with a sentence frame.
 */

export type ClaimKind = "hypothesis" | "question";

export interface SpecificAim {
  /** Stable key for the aim's card; not shown. */
  id: string;
  /** The aim itself, starting with an action verb. */
  aim: string;
  /** How: design, sample or system, main measure. */
  approach: string;
  /** What the writer expects, and what a different result would mean. */
  outcome: string;
}

export interface SpecificAimsInput {
  title: string;
  hook: string;
  known: string;
  gap: string;
  longTerm: string;
  objective: string;
  claimKind: ClaimKind;
  claim: string;
  rationale: string;
  aims: SpecificAim[];
  outcomes: string;
  impact: string;
  /** Words that fill one page in the writer's own template. */
  pageWords: string;
}

export const AIM_PARTS = ["opening", "objective", "aims", "closing"] as const;
export type AimPart = (typeof AIM_PARTS)[number];

/**
 * A typical split of the page, in percent. A planning aid: the aims carry the page
 * because reviewers read them first, and the opening and payoff stay short.
 */
export const AIM_SHARES: Record<AimPart, number> = {
  opening: 25,
  objective: 20,
  aims: 40,
  closing: 15,
};

export const MIN_AIMS = 1;
export const MAX_AIMS = 4;
/** A starting estimate for the writer to replace with a measured figure. */
export const DEFAULT_PAGE_WORDS = 700;
export const MIN_PAGE_WORDS = 200;
export const MAX_PAGE_WORDS = 1500;

/** Verbs that promise activity rather than a result; suggestions, not errors. */
const WEAK_VERBS: ReadonlySet<string> = new Set([
  "study",
  "look",
  "explore",
  "understand",
  "learn",
  "see",
]);
const FIRST_WORD = /^(?:to\s+)?(\p{L}+)/iu;

export function emptyAim(id: string): SpecificAim {
  return { id, aim: "", approach: "", outcome: "" };
}

export function pageCapacity(raw: string): number {
  const value = parseWholeCount(raw);
  if (value === null || value < MIN_PAGE_WORDS || value > MAX_PAGE_WORDS) {
    return DEFAULT_PAGE_WORDS;
  }
  return value;
}

function has(text: string): boolean {
  return text.trim() !== "";
}

function wordsOf(...texts: string[]): number {
  return texts.reduce((sum, text) => sum + countWords(text), 0);
}

function aimWords(aim: SpecificAim, number: number): number {
  const body = wordsOf(aim.aim, aim.approach, aim.outcome);
  return body === 0 ? 0 : body + countWords(`Aim ${number}.`);
}

/** The writer's words in each part of the page. */
export function partWords(input: SpecificAimsInput): Record<AimPart, number> {
  return {
    opening: wordsOf(input.hook, input.known, input.gap),
    objective: wordsOf(
      input.longTerm,
      input.objective,
      input.claim,
      input.rationale
    ),
    aims: input.aims.reduce(
      (sum, aim, index) => sum + aimWords(aim, index + 1),
      0
    ),
    closing: wordsOf(input.outcomes, input.impact),
  };
}

export interface AimsMeter {
  total: number;
  capacity: number;
  /** Words still free on the page; negative when over. */
  remaining: number;
  /** 0–100+ — not capped, so an overfull page reads as over. */
  percent: number;
  parts: Record<AimPart, number>;
  targets: Record<AimPart, number>;
}

export function measureSpecificAims(input: SpecificAimsInput): AimsMeter {
  const parts = partWords(input);
  const capacity = pageCapacity(input.pageWords);
  const total = AIM_PARTS.reduce((sum, part) => sum + parts[part], 0);
  const allocated = allocateWords(
    capacity,
    AIM_PARTS.map((part) => AIM_SHARES[part]),
    5
  );
  const targets = Object.fromEntries(
    AIM_PARTS.map((part, index) => [part, allocated[index]])
  ) as Record<AimPart, number>;
  return {
    total,
    capacity,
    remaining: capacity - total,
    percent: Math.round((total / capacity) * 100),
    parts,
    targets,
  };
}

export type CheckId =
  | "opening"
  | "objective"
  | "claim"
  | "aimCount"
  | "outcomes"
  | "verbs"
  | "closing"
  | "fit";

export interface AimCheck {
  id: CheckId;
  /** done: satisfied; todo: missing; note: worth a second look. */
  state: "done" | "todo" | "note";
  /** Aim numbers (from 1) a "verbs" or "outcomes" note refers to. */
  aims?: number[];
}

/** The first word of an aim, lower-cased, minus a leading "To". */
export function aimVerb(aim: string): string {
  return FIRST_WORD.exec(aim.trim())?.[1]?.toLowerCase() ?? "";
}

function filledAims(input: SpecificAimsInput): [SpecificAim, number][] {
  return input.aims
    .map((aim, index): [SpecificAim, number] => [aim, index + 1])
    .filter(([aim]) => has(aim.aim) || has(aim.approach) || has(aim.outcome));
}

function countCheck(count: number): AimCheck {
  if (count === 0) {
    return { id: "aimCount", state: "todo" };
  }
  return { id: "aimCount", state: count >= 2 ? "done" : "note" };
}

function fitState(meter: AimsMeter): AimCheck["state"] {
  if (meter.total === 0) {
    return "todo";
  }
  return meter.remaining >= 0 ? "done" : "note";
}

/** What is still missing or worth a second look, in the order a reviewer reads the page. */
export function specificAimsChecks(input: SpecificAimsInput): AimCheck[] {
  const filled = filledAims(input);
  const noOutcome = filled.filter(([aim]) => !has(aim.outcome));
  const weak = filled.filter(([aim]) => WEAK_VERBS.has(aimVerb(aim.aim)));
  const meter = measureSpecificAims(input);
  const checks: AimCheck[] = [
    {
      id: "opening",
      state: has(input.hook) && has(input.gap) ? "done" : "todo",
    },
    {
      id: "objective",
      state: has(input.objective) ? "done" : "todo",
    },
    { id: "claim", state: has(input.claim) ? "done" : "todo" },
    countCheck(filled.length),
    {
      id: "outcomes",
      state: filled.length > 0 && noOutcome.length === 0 ? "done" : "todo",
      aims: noOutcome.map(([, number]) => number),
    },
    {
      id: "verbs",
      state: weak.length === 0 ? "done" : "note",
      aims: weak.map(([, number]) => number),
    },
    { id: "closing", state: has(input.outcomes) ? "done" : "todo" },
    { id: "fit", state: fitState(meter) },
  ];
  return checks;
}

/* ------------------------------------------------------------------ document */

export interface SpecificAimsLabels {
  defaultTitle: string;
  subtitle: string;
  subtitleWords: string;
  headings: Record<AimPart, string>;
  target: string;
  tips: Record<AimPart, string>;
  prompts: {
    hook: string;
    known: string;
    gap: string;
    longTerm: string;
    objective: string;
    hypothesis: string;
    question: string;
    rationale: string;
    aim: string;
    approach: string;
    outcome: string;
    outcomes: string;
    impact: string;
  };
  aimLabel: string;
  outcomeLead: string;
}

type Format = (value: number, options?: Intl.NumberFormatOptions) => string;

interface Part {
  text: string;
  prompt: string;
}

/**
 * The writer's sentences for one paragraph, in order. Consecutive filled parts run
 * together as one paragraph (that is how they sit on the page); a missing part breaks
 * the run with a prompt in its place.
 */
export function paragraphFromParts(parts: readonly Part[]): DocBlock[] {
  const blocks: DocBlock[] = [];
  let run: string[] = [];
  const flush = () => {
    if (run.length > 0) {
      blocks.push({ kind: "paragraph", text: run.join(" ") });
      run = [];
    }
  };
  for (const part of parts) {
    const lines = splitLines(part.text);
    if (lines.length === 0) {
      flush();
      blocks.push({ kind: "prompt", text: part.prompt });
    } else {
      run.push(lines.join(" "));
    }
  }
  flush();
  return blocks;
}

function aimBlocks(
  aim: SpecificAim,
  number: number,
  labels: SpecificAimsLabels
): DocBlock[] {
  const lead: DocRun = { text: t(labels.aimLabel, { n: number }), bold: true };
  const statement = splitLines(aim.aim).join(" ");
  const how = splitLines(aim.approach).join(" ");
  const result = splitLines(aim.outcome).join(" ");
  const blocks: DocBlock[] = [];
  blocks.push(
    statement
      ? { kind: "paragraph", text: [lead, { text: ` ${statement}` }] }
      : { kind: "prompt", text: [lead, { text: ` ${labels.prompts.aim}` }] }
  );
  blocks.push(
    how
      ? { kind: "paragraph", text: how }
      : { kind: "prompt", text: labels.prompts.approach }
  );
  blocks.push(
    result
      ? {
          kind: "paragraph",
          text: [
            { text: `${labels.outcomeLead} `, italic: true },
            { text: result },
          ],
        }
      : { kind: "prompt", text: labels.prompts.outcome }
  );
  return blocks;
}

function sectionGuidance(
  part: AimPart,
  labels: SpecificAimsLabels,
  meter: AimsMeter,
  fmt: Format
): DocBlock {
  const target = t(labels.target, {
    words: fmt(meter.targets[part]),
    share: fmt(AIM_SHARES[part] / 100, { style: "percent" }),
  });
  return { kind: "guidance", text: `${target} ${labels.tips[part]}` };
}

export function buildSpecificAims(
  input: SpecificAimsInput,
  labels: SpecificAimsLabels,
  fmt: Format
): DocumentModel {
  const meter = measureSpecificAims(input);
  const claimPrompt =
    input.claimKind === "hypothesis"
      ? labels.prompts.hypothesis
      : labels.prompts.question;
  const bodies: Record<AimPart, DocBlock[]> = {
    opening: paragraphFromParts([
      { text: input.hook, prompt: labels.prompts.hook },
      { text: input.known, prompt: labels.prompts.known },
      { text: input.gap, prompt: labels.prompts.gap },
    ]),
    objective: paragraphFromParts([
      { text: input.longTerm, prompt: labels.prompts.longTerm },
      { text: input.objective, prompt: labels.prompts.objective },
      { text: input.claim, prompt: claimPrompt },
      { text: input.rationale, prompt: labels.prompts.rationale },
    ]),
    aims: input.aims.flatMap((aim, index) => aimBlocks(aim, index + 1, labels)),
    closing: paragraphFromParts([
      { text: input.outcomes, prompt: labels.prompts.outcomes },
      { text: input.impact, prompt: labels.prompts.impact },
    ]),
  };
  const sections: DocSection[] = AIM_PARTS.map((part) => ({
    heading: labels.headings[part],
    blocks: [sectionGuidance(part, labels, meter, fmt), ...bodies[part]],
  }));
  return {
    title: input.title.trim() || labels.defaultTitle,
    subtitle:
      meter.total > 0
        ? t(labels.subtitleWords, { words: fmt(meter.total) })
        : labels.subtitle,
    sections,
  };
}
